import React, { useState, useEffect, useRef } from "react";
import { graphql, Link, useStaticQuery } from "gatsby";
import * as c from "./mobileNavbarItems.module.scss";
import classNames from "classnames";
import Helmet from "react-helmet";
import { trackEvent } from "@utils/analytics";
import { Button } from "@components/shared";

// Define feature categories and their items in display order
const featureCategories = {
  "SCHEDULING & PAYMENTS": [
    "scheduling",
    "payments",
    "calendar-and-scheduling",
    "online-booking",
    "express-booking",
    "mobile-apps",
  ],
  "CLIENT RELATIONSHIPS": [
    "client-management",
    "call-text-chat",
    "memberships-and-packages",
    "integrated-forms",
    "gift-cards",
  ],
  "MARKETING & AUTOMATION": ["automated-flows", "campaigns", "offers-and-discounts", "virtual-waiting-room"],
  MANAGEMENT: ["retail-and-inventory", "staff-management", "reporting", "multi-location", "payroll-processing"],
};

const getFeatureUrl = (slug) => {
  if (slug === "payments") {
    return "/payments/";
  }
  return `/features/${slug}/`;
};

export default function MobileNavbarItems({ isMobileNavOpen, toggleMobileNav }) {
  const data = useStaticQuery(
    graphql`
      query {
        businessPages: allContentfulLandingPage(
          filter: { pageVariant: { in: ["Business Page"] }, slug: { ne: "why-mangomint" } }
        ) {
          nodes {
            title: navbarOverviewTitle
            slug
          }
        }
        featurePages: allContentfulLandingPage(
          filter: {
            pageVariant: { in: ["Showcase Page", "Feature Page"] }
            slug: { nin: ["marketing-and-automation"] }
          }
        ) {
          nodes {
            slug
            title: navbarOverviewTitle
          }
        }
        tags: contentfulNavbar {
          featuredPageTags {
            ... on ContentfulLandingPage {
              title: navbarOverviewTitle
              slug
            }
          }
          newPageTags {
            ... on ContentfulLandingPage {
              title: navbarOverviewTitle
              slug
            }
          }
        }
      }
    `
  );
  const featurePages = data.featurePages.nodes;
  const { featuredPageTags, newPageTags } = data.tags;

  const isFeatured = (slug) => featuredPageTags?.some((tag) => tag.slug === slug) || false;
  const isNew = (slug) => newPageTags?.some((tag) => tag.slug === slug) || false;

  // TODO: Consolidate this. Copy-pasted from solutions overview page
  const solutionsOrder = [
    "hair-salon-software",
    "spa-software",
    "nail-salon-software",
    "medical-spa-software",
    "beauty-studio-software",
    "barbershop-software",
    "massage-studio-software",
    "tattoo-piercing-studio-software",
    "wellness-center-software",
    "tanning-salon-software",
    "hair-removal-studio-software",
    "lash-and-brow-software",
    "skincare-studio-software",
    "iv-therapy-software",
  ];

  const excludedSlugs = new Set(["dog-groomers", "salon-management-software"]);
  const slugOrderMap = Object.fromEntries(solutionsOrder.map((slug, index) => [slug, index]));

  const allSolutions = data?.businessPages?.nodes
    .filter(({ slug }) => !excludedSlugs.has(slug))
    .sort((a, b) => (slugOrderMap[a.slug] ?? Infinity) - (slugOrderMap[b.slug] ?? Infinity));

  const MenuItem = ({ to, label, isExpandable, isExternal, children }) => {
    const [isOpen, setOpen] = useState(false);
    const [toggleTimeout, setToggleTimeout] = useState(false);

    const cx = classNames(c.menuItem, "mobile-menu-item", {
      [c.expandable]: isExpandable,
      [c.open]: isOpen,
      [c.active]: isMobileNavOpen,
    });

    let submenu;
    if (isExpandable) {
      submenu = <ul className={c.submenu}>{children}</ul>;
    }

    return (
      <li
        className={cx}
        onClick={() => {
          !toggleTimeout && setOpen(!isOpen);
          setToggleTimeout(true);
          setTimeout(() => {
            // Prevent double click from closing and opening the menu to quickly
            setToggleTimeout(false);
          }, 300);
        }}
      >
        <MenuLink to={to} isExpandable={isExpandable} isExternal={isExternal}>
          {label}
        </MenuLink>
        {submenu}
      </li>
    );
  };

  const SubMenuItem = ({ to, label, isExternal }) => {
    const noTextTransform = ["/payments/"];
    const shouldRemoveTextTransform = noTextTransform.includes(to);

    return (
      <li className={c.submenuItem}>
        <MenuLink
          to={to}
          className={classNames(c.submenuLink, { [c.noTextTransform]: shouldRemoveTextTransform })}
          isExternal={isExternal}
        >
          {label}
        </MenuLink>
      </li>
    );
  };

  const CategoryHeader = ({ label }) => {
    return (
      <li className={c.categoryHeader}>
        <span className="eyebrow2">{label}</span>
      </li>
    );
  };

  const MenuLink = ({ className, to, isExternal, isExpandable, children }) => {
    if (isExternal) {
      return (
        <a
          className={classNames(className, c.menuLink)}
          href={to}
          target="_blank"
          onClick={toggleMobileNav}
          rel="noreferrer"
        >
          {children}
        </a>
      );
    } else if (isExpandable) {
      return (
        <div className={classNames(className, c.menuLink)}>
          {children} <span className={c.arrowIcon} />
        </div>
      );
    } else {
      return (
        <Link className={classNames(className, c.menuLink)} to={to} onClick={toggleMobileNav}>
          {children}
        </Link>
      );
    }
  };

  // Helper function to get features for a specific category
  const getCategoryFeatures = (category) => {
    const categoryFeatureSlugs = featureCategories[category] || [];
    return featurePages
      .filter((feature) => categoryFeatureSlugs.includes(feature.slug))
      .sort((a, b) => {
        const indexA = categoryFeatureSlugs.indexOf(a.slug);
        const indexB = categoryFeatureSlugs.indexOf(b.slug);
        return indexA - indexB;
      });
  };

  const navRef = useRef(null);
  const ulRef = useRef(null);

  useEffect(() => {
    document.querySelectorAll(".mobile-menu-item").forEach((navItem, index) => {
      navItem.style.setProperty("--delay", `${0.3 + index * 0.05}s`);
    });
  }, [isMobileNavOpen]);

  return (
    <>
      <Helmet htmlAttributes={{ class: classNames({ [c.disableDocumentScroll]: isMobileNavOpen }) }} />
      <div ref={navRef} className={classNames(c.container, { [c.open]: isMobileNavOpen })}>
        <ul ref={ulRef} id="mobile-nav-list" className={c.menu}>
          <MenuItem label="Businesses" isExpandable={true}>
            {allSolutions.map((solution) => (
              <SubMenuItem key={solution.slug} label={solution.title} to={`/solutions/${solution.slug}/`} />
            ))}
          </MenuItem>

          <MenuItem label="Features" isExpandable={true}>
            {Object.keys(featureCategories).map((category) => (
              <React.Fragment key={category}>
                <CategoryHeader label={category} />
                {getCategoryFeatures(category).map((feature) => (
                  <SubMenuItem
                    key={feature.slug}
                    label={feature.title}
                    to={getFeatureUrl(feature.slug)}
                    showBadgeFeatured={isFeatured(feature.slug)}
                    showBadgeNew={isNew(feature.slug)}
                  />
                ))}
              </React.Fragment>
            ))}
          </MenuItem>

          <MenuItem label="Pricing" to="/pricing/" />

          <MenuItem label="Why Mangomint" to="/why-mangomint/" />

          <MenuItem label="Login" to="https://app.mangomint.com" isExternal={true} />
          <li
            className={classNames("mobile-menu-item", c.menuItemDemo, c.menuButton, {
              [c.active]: isMobileNavOpen,
            })}
          >
            <Button
              linkTo="/demo/"
              variant="outline"
              className={c.menuCtaDemo}
              onClick={() => trackEvent("Mobile Nav: Demo CTA Clicked")}
            >
              Book a demo
            </Button>
          </li>
          <li className={classNames("mobile-menu-item", c.menuItemTry, c.menuButton, { [c.active]: isMobileNavOpen })}>
            <Button
              linkTo="/trial/"
              variant="full"
              className={c.menuCtaTry}
              onClick={() => trackEvent("Mobile Nav: Trial CTA Clicked")}
            >
              Try it now
            </Button>
          </li>
        </ul>
      </div>
    </>
  );
}
